import { useState } from 'react';
import { X, Lock, User, Mail } from 'lucide-react';

interface TokenInfo {
  status: 'existing_account' | 'new_account';
  email: string;
  display_name?: string;
  message: string;
}

interface Props {
  tokenInfo: TokenInfo;
  isProcessing: boolean;
  error: string | null;
  onSubmit: (password: string, emailOverride?: string) => Promise<boolean>;
  onCancel: () => void;
  emailEditable?: boolean;
}

export default function TalkoruPasswordModal({
  tokenInfo,
  isProcessing,
  error,
  onSubmit,
  onCancel,
  emailEditable = false,
}: Props) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const isNewAccount = tokenInfo.status === 'new_account';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (emailEditable) {
      if (!emailOverride.trim()) {
        setLocalError('Email address is required');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOverride.trim())) {
        setLocalError('Please enter a valid email address');
        return;
      }
    }

    if (!password) {
      setLocalError('Password is required');
      return;
    }

    if (isNewAccount) {
      if (password.length < 6) {
        setLocalError('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }
    }

    await onSubmit(password, emailEditable ? emailOverride.trim() : undefined);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: 20
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        maxWidth: 400,
        width: '100%',
        padding: 24,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isNewAccount ? 'Create Your Account' : 'Link Your Account'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              color: '#666'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* User info */}
        <div style={{
          background: '#f8fafc',
          borderRadius: 12,
          padding: 16,
          marginBottom: 20
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: emailEditable ? 0 : 8 }}>
            <User size={18} style={{ color: '#22c55e' }} />
            <span style={{ fontWeight: 500 }}>{tokenInfo.display_name || 'User'}</span>
          </div>
          {!emailEditable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <Mail size={18} style={{ color: '#22c55e' }} />
              <span style={{ color: '#666' }}>{tokenInfo.email}</span>
            </div>
          )}
        </div>

        {/* Email input for WhatsApp/generated accounts */}
        {emailEditable && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              background: '#fef9c3',
              border: '1px solid #fde047',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 12,
              fontSize: '0.85rem',
              color: '#713f12'
            }}>
              Your account uses a generated email. Enter your real email address to use with AILabQuant.
            </div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>
              Your Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }} />
              <input
                type="email"
                value={emailOverride}
                onChange={(e) => setEmailOverride(e.target.value)}
                placeholder="you@example.com"
                disabled={isProcessing}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  borderRadius: 8,
                  border: '2px solid #e5e7eb',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        )}

        {/* Message */}
        <p style={{ color: '#666', marginBottom: 20, fontSize: '0.9rem' }}>
          {isNewAccount
            ? 'Create a password to access AILabQuant directly in the future.'
            : 'Enter your AILabQuant password to link your accounts securely.'}
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>
              {isNewAccount ? 'Create Password' : 'AILabQuant Password'}
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isNewAccount ? 'Choose a password' : 'Enter your password'}
                disabled={isProcessing}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  borderRadius: 8,
                  border: '2px solid #e5e7eb',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {isNewAccount && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.9rem' }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999'
                }} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  disabled={isProcessing}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: 8,
                    border: '2px solid #e5e7eb',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          )}

          {/* Error message */}
          {(error || localError) && (
            <div style={{
              background: '#fef2f2',
              color: '#dc2626',
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
              fontSize: '0.9rem'
            }}>
              {localError || error}
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isProcessing}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                background: 'white',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '1rem'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#22c55e',
                color: 'white',
                cursor: isProcessing ? 'wait' : 'pointer',
                fontWeight: 500,
                fontSize: '1rem',
                opacity: isProcessing ? 0.7 : 1
              }}
            >
              {isProcessing ? 'Please wait...' : (isNewAccount ? 'Create Account' : 'Link Accounts')}
            </button>
          </div>
        </form>

        {/* Footer hint */}
        {!isNewAccount && (
          <p style={{
            marginTop: 16,
            fontSize: '0.8rem',
            color: '#999',
            textAlign: 'center'
          }}>
            Forgot your password? Log in to AILabQuant directly to reset it.
          </p>
        )}
      </div>
    </div>
  );
}
